import { Chip, IconButton } from '@assembly-js/design-system';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MessageNotificationProps,
  useChannelActionContext,
  useChannelStateContext,
} from 'stream-chat-react';

import { useMessagingTheme } from 'src/features/messages/messagingThemeContext';
import { useAppSelector } from 'src/hooks/useStore';

const UnreadNotificationChip = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => {
  const { isSidebarTheme } = useMessagingTheme();
  return (
    <Chip
      className={clsx(
        'text-base px-2 py-[5px]',
        isSidebarTheme ? 'bg-gray-100 border-non-hover-border' : 'bg-white',
      )}
      label={label}
      onClick={onClick}
      prefixIcon="ArrowDownSolid"
      variant="outlined"
    />
  );
};

const notificationPositioningStyles = 'absolute self-center bottom-0.5 z-10';

const UnreadNotifications = ({ unreadCount }: { unreadCount: number }) => {
  const currentUserGetStreamId = useAppSelector(
    (state) => state.user.data?.getstreamId || '',
  );
  const { channel, channelUnreadUiState } = useChannelStateContext();
  const { jumpToMessage, jumpToFirstUnreadMessage, markRead } =
    useChannelActionContext();
  const { t } = useTranslation();

  const [unreadMentionsCount, setUnreadMentionsCount] = useState(() =>
    channel.countUnreadMentions(),
  );
  useEffect(() => {
    const count = channel.countUnreadMentions();
    setUnreadMentionsCount(count);
  }, [channel, unreadCount]);

  const getFirstUnreadMention = useCallback(async () => {
    if (!channelUnreadUiState) {
      return null;
    }

    try {
      // Query messages starting from the first unread message
      const response = await channel.query({
        messages: {
          created_at_after: channelUnreadUiState.last_read,
        },
      });

      // Find the first message that mentions the current user
      const firstUnreadMention = response.messages.find((message) =>
        message.mentioned_users?.some(
          (user) => user.id === currentUserGetStreamId,
        ),
      );

      return firstUnreadMention || null;
    } catch (error) {
      console.error('Error querying unread messages:', error);
      return null;
    }
  }, [channel, currentUserGetStreamId, channelUnreadUiState]);

  return (
    <div
      className={clsx(
        'flex flex-col gap-2 justify-center items-center',
        notificationPositioningStyles,
      )}
    >
      {unreadMentionsCount > 0 && (
        <UnreadNotificationChip
          label={t(($) => $.messages.unread_mentions, {
            count: unreadMentionsCount,
          })}
          onClick={() => {
            void (async () => {
              const firstUnreadMention = await getFirstUnreadMention();
              if (firstUnreadMention) {
                await jumpToMessage(firstUnreadMention.id);
                markRead();
              } else {
                await jumpToFirstUnreadMessage();
                markRead();
              }
            })();
          }}
        />
      )}
      <UnreadNotificationChip
        label={t(($) => $.messages.unread_messages, { count: unreadCount })}
        onClick={() => {
          void jumpToFirstUnreadMessage().then(() => markRead());
        }}
      />
    </div>
  );
};

// based on the default implementations for
// MessageNotification: https://github.com/GetStream/stream-chat-react/blob/master/src/components/MessageList/MessageNotification.tsx
// ScrollToBottom: https://github.com/GetStream/stream-chat-react/blob/master/src/components/MessageList/ScrollToBottomButton.tsx
// UnreadMessagesNotification: https://github.com/GetStream/stream-chat-react/blob/master/src/components/MessageList/UnreadMessagesNotification.tsx
export const CustomMessageNotification = ({
  isMessageListScrolledToBottom,
  onClick,
  unreadCount,
  showNotification,
}: MessageNotificationProps) => {
  const { isSidebarTheme } = useMessagingTheme();

  if (unreadCount && unreadCount > 0 && showNotification) {
    return <UnreadNotifications unreadCount={unreadCount} />;
  }

  if (!isMessageListScrolledToBottom) {
    return (
      <IconButton
        className={clsx(
          notificationPositioningStyles,
          isSidebarTheme &&
            'bg-gray-100 border-non-hover-border hover:bg-secondary-hover',
        )}
        icon="ArrowDownSolid"
        label="Scroll to bottom"
        onClick={onClick}
        variant="secondary"
        aria-live="polite"
      />
    );
  }

  return null;
};
